import { ReactNode } from "react";
import t from "@/lang/lang";
import {
  ListRequest,
  useHtzSummaryBaseDataListQuery,
} from "@/services/backend/htz/summary/api/base-data-api";
import {
  SummaryBaseData,
  htzSummaryBaseDataKey,
} from "@/services/backend/htz/summary/types/base-data";
import { DeleteSummaryBaseDataDialog } from "@/services/backend/htz/summary/components/delete-summary-base-data-dialog";
import { DefaultBaseDataCheckbox } from "@/services/backend/shared/base-data-kit/components/default-base-data-checkbox";
import { useListCompanyQuery } from "@/services/backend/company/api/company";
import { CompanyFilter } from "@/services/backend/company/components/company-filter";
import { CompanyName } from "@/services/backend/company/components/company-name";
import { SearchFilter } from "@/shared/components/filter/search-filter";
import { PaginationLimitOffset } from "@/shared/components/tables/pagination-limit-offset";
import { TableCard } from "@/shared/components/tables/table-card";
import { TableEmptyRow } from "@/shared/components/tables/table-empty-row";
import { TableLoadingRow } from "@/shared/components/tables/table-loading-row";
import { TableRtkErrorRow } from "@/shared/components/tables/table-rtk-error-row";
import { Button } from "@/shared/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/shared/components/ui/table";

export function SummaryBaseDataTable({
  request,
  onRequestChange,
  renderRowLink,
  hidePagination = false,
  hideBorder = false,
}: {
  request: ListRequest;
  onRequestChange: (request: ListRequest) => void;
  renderRowLink: (baseDataId: string, children: ReactNode) => ReactNode;
  hidePagination?: boolean;
  hideBorder?: boolean;
}) {
  // The backend requires an active company filter with at least one
  // company, so an inactive filter falls back to every company the user
  // may see. Until those are known there is no request to send.
  const { data: companyList } = useListCompanyQuery({});
  const companyIds = companyList?.companies.map((c) => c.id) ?? [];
  const companiesKnown = companyIds.length > 0;
  const effectiveRequest: ListRequest = request.company.active
    ? request
    : { ...request, company: { active: true, values: companyIds } };

  const { data, isLoading, error } = useHtzSummaryBaseDataListQuery(
    effectiveRequest,
    { skip: !request.company.active && !companiesKnown },
  );

  const emptyList = !isLoading && !error && data?.items.length === 0;

  return (
    <div className="grid gap-2">
      <TableCard className={hideBorder ? "border-none shadow-none" : ""}>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t("Aktiv")}</TableHead>
              <TableHead>{t("Bezeichnung")}</TableHead>
              <TableHead>{t("Unternehmen")}</TableHead>
              <TableHead>{t("Aktionen")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading && <TableLoadingRow colSpan={4} />}
            {error && <TableRtkErrorRow error={error} colSpan={4} />}
            {emptyList && <TableEmptyRow colSpan={4} />}
            {data &&
              data.items.map((baseData) => (
                <SummaryBaseDataTableRow
                  key={baseData.id}
                  baseData={baseData}
                  renderRowLink={renderRowLink}
                />
              ))}
          </TableBody>
        </Table>
      </TableCard>
      {!hidePagination && data && !emptyList && (
        <div className="flex items-center">
          <PaginationLimitOffset
            offset={request.offset ?? 0}
            limit={request.limit ?? 0}
            totalCount={data.totalCount}
            setLimitOffset={(limit, offset) =>
              onRequestChange({ ...request, limit, offset })
            }
            className="ml-auto"
          />
        </div>
      )}
    </div>
  );
}

function SummaryBaseDataTableRow({
  baseData,
  renderRowLink,
}: {
  baseData: SummaryBaseData;
  renderRowLink: (baseDataId: string, children: ReactNode) => ReactNode;
}) {
  return (
    <TableRow>
      <TableCell>
        <div className="ml-4">
          <DefaultBaseDataCheckbox
            subdomain="htz"
            dataKey={htzSummaryBaseDataKey}
            companyId={baseData.companyId}
            id={baseData.id}
          />
        </div>
      </TableCell>
      <TableCell>
        {renderRowLink(
          baseData.id,
          <Button variant="link">{baseData.label}</Button>,
        )}
      </TableCell>
      <TableCell>
        <CompanyName companyId={baseData.companyId} />
      </TableCell>
      <TableCell>
        <DeleteSummaryBaseDataDialog baseData={baseData} />
      </TableCell>
    </TableRow>
  );
}

export function SummaryBaseDataTableFilters({
  request,
  onRequestChange,
  hideCompanyFilter,
}: {
  request: ListRequest;
  onRequestChange: (request: ListRequest) => void;
  hideCompanyFilter?: boolean;
}) {
  return (
    <div className="flex items-center gap-2">
      <SearchFilter
        filter={request.label ?? { active: false, values: [] }}
        onFilterChange={(label) => onRequestChange({ ...request, label })}
        placeholder={t("Nach Bezeichnung suchen ...")}
        className="h-8"
      />
      <CompanyFilter
        filter={request.company}
        onFilterChange={(company) => onRequestChange({ ...request, company })}
        hidden={hideCompanyFilter}
      />
    </div>
  );
}

export function useDefaultHtzSummaryBaseDataListRequest(): ListRequest {
  return {
    label: { active: false, values: [] },
    company: { active: false, values: [] },
    limit: 50,
    offset: 0,
  };
}
